import React, { useState, useEffect, useRef } from 'react';
import { Video, Camera, MapPin, AlertTriangle, Maximize2, Play, Pause, Settings, Grid3x3, Layers, Zap, TrendingUp, Activity, CheckCircle } from 'lucide-react';
import sitemap from '../../assets/images/sitemap.webp';
import helmetDetect01 from '../../assets/videos/helmet_detect_01.mp4';
import helmetDetect02 from '../../assets/videos/helmet_detect_02.mp4';
import helmetDetect03 from '../../assets/videos/helmet_detect_03.mp4';
import helmetDetect04 from '../../assets/videos/helmet_detect_04.mp4';
import helmetDetect05 from '../../assets/videos/helmet_detect_05.mp4';
import detectionAlert from '../../assets/images/detection_alert.png';

export default function AIVisionIntegrationControlCenter() {
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [activeView, setActiveView] = useState('grid');
  const [isPlaying, setIsPlaying] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);

  // Mock camera feeds data
  const cameras = [
    {
      id: 'CAM-004',
      name: 'Assembly Line',
      location: 'Plant 1 - Assembly Line',
      status: 'active',
      streamUrl: helmetDetect01,
      coordinates: { x: 20, y: 30 },
      // aiDetections: 0,
      uptime: '99.8%',
      aiDetections: 2,
      resolution: '1920x1080'
    },
    {
      id: 'CAM-002',
      name: 'Construction Site 1',
      location: 'Plant 1 - Construction Site 1',
      status: 'active',
      streamUrl: helmetDetect02,
      coordinates: { x: 45, y: 40 },
      aiDetections: 1,
      uptime: '99.9%',
      resolution: '1920x1080'
    },
    {
      id: 'CAM-003',
      name: 'Warehouse 1',
      location: 'Plant 2 - Warehouse 1',
      status: 'active',
      streamUrl: helmetDetect03,
      coordinates: { x: 70, y: 25 },
      // aiDetections: 1,
      uptime: '100%',
      resolution: '2560x1440'
    },
    {
      id: 'CAM-004',
      name: 'Construction Site 2',
      location: 'Warehouse 1 - Construction Site 2',
      status: 'active',
      streamUrl: helmetDetect04,
      coordinates: { x: 30, y: 65 },
      aiDetections: 0,
      uptime: '98.5%',
      resolution: '1920x1080'
    },
    {
      id: 'CAM-005',
      name: 'Maintenance Workshop',
      location: 'Plant 1 - Workshop',
      status: 'active',
      streamUrl: helmetDetect05,
      coordinates: { x: 60, y: 55 },
      // aiDetections: 3,
      uptime: '99.7%',
      resolution: '1920x1080'
    },
    // {
    //   id: 'CAM-006',
    //   name: 'Electrical Room',
    //   location: 'Plant 1 - Electrical',
    //   status: 'active',
    //   streamUrl: '/assets/videos/helmet_detect_02.mp4',
    //   coordinates: { x: 80, y: 45 },
    //   aiDetections: 0,
    //   uptime: '100%',
    //   resolution: '1920x1080'
    // }
  ];

  // Mock AI detection alerts
  const aiAlerts = [
    {
      id: 'ALERT-001',
      cameraId: 'CAM-002',
      cameraName: 'Assembly Line',
      timestamp: '2025-12-06 09:45:23',
      type: 'Assembly Line',
      severity: 'High',
      confidence: 94.5,
      description: 'Two Worker without safety helmet detected',
      status: 'unverified',
      autoAction: 'Alert sent to supervisor',
      imageUrl: detectionAlert
    },
    {
      id: 'ALERT-002',
      cameraId: 'CAM-005',
      cameraName: 'Maintenance Workshop',
      timestamp: '2025-12-06 09:38:15',
      type: 'Unsafe Act',
      severity: 'Critical',
      confidence: 91.2,
      description: 'Worker in restricted zone without authorization',
      status: 'unverified',
      autoAction: 'Security notified',
      // imageUrl: '/assets/images/detection_alert.png'
    },
    {
      id: 'ALERT-003',
      cameraId: 'CAM-003',
      cameraName: 'Chemical Storage Zone',
      timestamp: '2025-12-06 09:22:47',
      type: 'Spill Detection',
      severity: 'Medium',
      confidence: 87.8,
      description: 'Potential chemical spill detected',
      status: 'verified',
      autoAction: 'Response team dispatched',
      // imageUrl: '/assets/images/detection_alert.png'
    },
    {
      id: 'ALERT-004',
      cameraId: 'CAM-002',
      cameraName: 'Production Floor A',
      timestamp: '2025-12-06 09:15:32',
      type: 'Equipment Misuse',
      severity: 'High',
      confidence: 89.3,
      description: 'Forklift operating at unsafe speed',
      status: 'unverified',
      autoAction: 'Alert pending review',
      // imageUrl: '/assets/images/detection_alert.png'
    },
    {
      id: 'ALERT-005',
      cameraId: 'CAM-005',
      cameraName: 'Maintenance Workshop',
      timestamp: '2025-12-06 08:58:19',
      type: 'Fire Hazard',
      severity: 'Critical',
      confidence: 96.7,
      description: 'Smoke detected near welding area',
      status: 'verified',
      autoAction: 'Fire safety protocol initiated',
      // imageUrl: '/assets/images/detection_alert.png'
    }
  ];

  // Mock system metrics
  const systemMetrics = {
    activeCameras: 6,
    totalCameras: 6,
    aiDetectionsToday: 28,
    verifiedAlerts: 15,
    falsePositives: 3,
    systemUptime: '99.8%',
    avgConfidence: 91.2,
    responseTime: '12 sec'
  };

  const getSeverityColor = (severity) => {
    const colors = {
      Critical: 'bg-red-100 text-red-800 border-red-300',
      High: 'bg-orange-100 text-orange-800 border-orange-300',
      Medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      Low: 'bg-green-100 text-green-800 border-green-300'
    };
    return colors?.[severity] || colors?.Medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      unverified: 'bg-yellow-100 text-yellow-800',
      verified: 'bg-green-100 text-green-800',
      dismissed: 'bg-gray-100 text-gray-800',
      false_positive: 'bg-red-100 text-red-800'
    };
    return colors?.[status] || colors?.unverified;
  };

  const handleVerifyAlert = (alertId) => {
    console.log('Verifying alert:', alertId);
    // Implementation would handle alert verification
  };

  const handleDismissAlert = (alertId) => {
    console.log('Dismissing alert:', alertId);
    // Implementation would handle alert dismissal
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">AI Vision Integration Control Center</h1>
              <p className="text-sm text-gray-400 mt-1">Real-time monitoring with automated safety detection</p>
            </div>
            <div className="flex items-center gap-3">
              <button className="px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2">
                <Settings size={18} />
                <span>System Settings</span>
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
                <Video size={18} />
                <span>Record Session</span>
              </button>
            </div>
          </div>

          {/* System Metrics */}
          <div className="grid grid-cols-8 gap-3 mt-6">
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Camera size={16} className="text-green-400" />
                <span className="text-xs font-medium text-gray-300">Active Cameras</span>
              </div>
              <div className="text-xl font-bold text-white">{systemMetrics?.activeCameras}/{systemMetrics?.totalCameras}</div>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Zap size={16} className="text-yellow-400" />
                <span className="text-xs font-medium text-gray-300">AI Detections</span>
              </div>
              <div className="text-xl font-bold text-white">{systemMetrics?.aiDetectionsToday}</div>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle size={16} className="text-green-400" />
                <span className="text-xs font-medium text-gray-300">Verified</span>
              </div>
              <div className="text-xl font-bold text-white">{systemMetrics?.verifiedAlerts}</div>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle size={16} className="text-red-400" />
                <span className="text-xs font-medium text-gray-300">False Positives</span>
              </div>
              <div className="text-xl font-bold text-white">{systemMetrics?.falsePositives}</div>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Activity size={16} className="text-blue-400" />
                <span className="text-xs font-medium text-gray-300">Uptime</span>
              </div>
              <div className="text-lg font-bold text-white">{systemMetrics?.systemUptime}</div>
            </div>
            <div className="bg-gray-700 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp size={16} className="text-purple-400" />
                <span className="text-xs font-medium text-gray-300">Avg Confidence</span>
              </div>
              <div className="text-lg font-bold text-white">{systemMetrics?.avgConfidence}%</div>
            </div>
            <div className="bg-gray-700 rounded-lg p-3 col-span-2">
              <div className="flex items-center gap-2 mb-1">
                <Zap size={16} className="text-orange-400" />
                <span className="text-xs font-medium text-gray-300">Response Time</span>
              </div>
              <div className="text-lg font-bold text-white">{systemMetrics?.responseTime}</div>
            </div>
          </div>

          {/* View Controls */}
          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={() => setActiveView('grid')}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                activeView === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <Grid3x3 size={18} />
              <span>Grid View</span>
            </button>
            <button
              onClick={() => setActiveView('map')}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                activeView === 'map' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <MapPin size={18} />
              <span>Site Map</span>
            </button>
            <button
              onClick={() => setActiveView('alerts')}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                activeView === 'alerts' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <AlertTriangle size={18} />
              <span>Alert Dashboard</span>
            </button>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="p-6">
        {activeView === 'grid' && (
          <div className="grid grid-cols-3 gap-4">
            {cameras?.map((camera) => (
              <div
                key={camera?.id}
                onClick={() => setSelectedCamera(camera)}
                className={`bg-gray-800 border rounded-lg overflow-hidden cursor-pointer transition-all hover:shadow-xl ${
                  selectedCamera?.id === camera?.id ? 'border-blue-500 shadow-xl' : 'border-gray-700'
                }`}
              >
                {/* Camera Feed */}
                <div className="relative aspect-video bg-gray-900">
                  <video
                    src={camera?.streamUrl}
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />

                  {/* AI Detection Overlay */}
                  {camera?.aiDetections > 0 && (
                    <div className="absolute top-2 right-2 bg-red-600 text-white px-2 py-1 rounded-lg text-xs font-bold flex items-center gap-1 animate-pulse">
                      <AlertTriangle size={14} />
                      <span>{camera?.aiDetections} Detection{camera?.aiDetections > 1 ? 's' : ''}</span>
                    </div>
                  )}

                  {/* Recording Indicator */}
                  <div className="absolute top-2 left-2 flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-xs font-medium text-white">LIVE</span>
                  </div>

                  {/* Playback Controls */}
                  <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between bg-black bg-opacity-50 rounded px-2 py-1">
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        setIsPlaying(!isPlaying);
                      }}
                      className="text-white hover:text-blue-400 transition-colors"
                    >
                      {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                    </button>
                    <button
                      onClick={(e) => {
                        e?.stopPropagation();
                        setSelectedCamera(camera);
                      }}
                      className="text-white hover:text-blue-400 transition-colors"
                    >
                      <Maximize2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Camera Info */}
                <div className="p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-white">{camera?.name}</h3>
                      <p className="text-xs text-gray-400 mt-1">{camera?.id}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      <span className="text-xs text-green-400">Active</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <MapPin size={12} />
                    <span>{camera?.location}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-700">
                    <span className="text-xs text-gray-400">Uptime: {camera?.uptime}</span>
                    <span className="text-xs text-gray-400">{camera?.resolution}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeView === 'map' && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4">Interactive Site Map</h3>
            <div className="relative bg-gray-900 rounded-lg" style={{ height: '600px' }}>
              {/* Site Map Placeholder */}
              <img src={sitemap} alt="site map" />

              {/* Camera Markers */}
              {cameras?.map((camera) => (
                <button
                  key={camera?.id}
                  onClick={() => setSelectedCamera(camera)}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 group"
                  style={{ left: `${camera?.coordinates?.x}%`, top: `${camera?.coordinates?.y}%` }}
                >
                  <div className={`relative ${camera?.aiDetections > 0 ? 'animate-pulse' : ''}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      camera?.aiDetections > 0 ? 'bg-red-600' : 'bg-blue-600'
                    } border-2 border-white shadow-lg`}>
                      <Camera size={20} />
                    </div>
                    {camera?.aiDetections > 0 && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold border-2 border-gray-900">
                        {camera?.aiDetections}
                      </div>
                    )}
                  </div>
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 bg-gray-800 px-3 py-2 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-gray-700">
                    <div className="text-xs font-semibold text-white">{camera?.name}</div>
                    <div className="text-xs text-gray-400">{camera?.id}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {activeView === 'alerts' && (
          <div className="grid grid-cols-12 gap-6">
            {/* Alert List */}
            <div className="col-span-5 space-y-3">
              <h3 className="text-lg font-bold text-white mb-4">AI Detection Alerts</h3>
              {aiAlerts?.map((alert) => (
                <div
                  key={alert?.id}
                  onClick={() => setSelectedAlert(alert)}
                  className={`bg-gray-800 border rounded-lg p-4 cursor-pointer transition-all hover:shadow-lg ${
                    selectedAlert?.id === alert?.id ? 'border-blue-500 shadow-lg' : 'border-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-mono text-gray-400">{alert?.id}</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${getSeverityColor(alert?.severity)}`}>
                          {alert?.severity}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(alert?.status)}`}>
                          {alert?.status === 'unverified' ? 'Needs Review' : 'Verified'}
                        </span>
                      </div>
                      <h4 className="font-semibold text-white mb-1">{alert?.type}</h4>
                      <p className="text-sm text-gray-400 mb-2">{alert?.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-blue-400">{alert?.confidence}%</div>
                      <div className="text-xs text-gray-500">Confidence</div>
                    </div>
                  </div>

                  <div className="space-y-1 text-sm text-gray-400">
                    <div className="flex items-center gap-2">
                      <Camera size={14} />
                      <span>{alert?.cameraName}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Activity size={14} />
                      <span>{alert?.timestamp}</span>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <div className="text-xs text-gray-500">
                      <span className="font-medium">Auto Action:</span> {alert?.autoAction}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Alert Detail */}
            <div className="col-span-7">
              {selectedAlert ? (
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-white mb-2">{selectedAlert?.type}</h2>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono text-gray-400">{selectedAlert?.id}</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${getSeverityColor(selectedAlert?.severity)}`}>
                          {selectedAlert?.severity}
                        </span>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-400">{selectedAlert?.confidence}%</div>
                      <div className="text-xs text-gray-500">AI Confidence</div>
                    </div>
                  </div>

                  {/* Video Frame Placeholder */}
                  <div className="bg-gray-900 rounded-lg mt-3 mb-6" style={{ height: '350px' }}>
                    <div className="h-full flex items-center justify-center">
                      {/* <div className="text-center">
                        <Video size={64} className="mx-auto mb-3 text-gray-600" />
                        <p className="text-gray-400">Detection Frame Snapshot</p>
                        <p className="text-sm text-gray-500 mt-2">AI-annotated image with detection highlights</p>
                      </div> */}
                      <img src={selectedAlert?.imageUrl} alt="detection alert" className="w-full h-full object-fill" />
                    </div>
                  </div>

                  {/* Alert Details */}
                  <div className="bg-gray-900 rounded-lg p-4 mb-6">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="text-sm font-medium text-gray-400">Camera</label>
                        <p className="text-white mt-1">{selectedAlert?.cameraName}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-400">Timestamp</label>
                        <p className="text-white mt-1">{selectedAlert?.timestamp}</p>
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="text-sm font-medium text-gray-400">Description</label>
                      <p className="text-white mt-1">{selectedAlert?.description}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-400">Automated Action</label>
                      <p className="text-white mt-1">{selectedAlert?.autoAction}</p>
                    </div>
                  </div>

                  {/* Verification Actions */}
                  {selectedAlert?.status === 'unverified' && (
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleDismissAlert(selectedAlert?.id)}
                        className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center gap-2 font-medium"
                      >
                        <span>Mark as False Positive</span>
                      </button>
                      <button
                        onClick={() => handleVerifyAlert(selectedAlert?.id)}
                        className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
                      >
                        <CheckCircle size={20} />
                        <span>Verify & Create Task</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-gray-800 border border-gray-700 rounded-lg p-12 text-center">
                  <div className="max-w-md mx-auto">
                    <AlertTriangle size={48} className="mx-auto mb-4 text-gray-600" />
                    <h3 className="text-lg font-semibold text-white mb-2">No Alert Selected</h3>
                    <p className="text-gray-400">
                      Select an alert from the list to view details and take action
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Selected Camera Fullscreen View */}
        {selectedCamera && activeView !== 'alerts' && (
          <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-6">
            <div className="bg-gray-800 rounded-lg max-w-6xl w-full">
              <div className="flex items-center justify-between p-4 border-b border-gray-700">
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedCamera?.name}</h3>
                  <p className="text-sm text-gray-400">{selectedCamera?.id} - {selectedCamera?.location}</p>
                </div>
                <button
                  onClick={() => setSelectedCamera(null)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <Maximize2 size={24} />
                </button>
              </div>
              <div className="p-6">
                <div className="bg-gray-900 rounded-lg aspect-video overflow-hidden">
                  <video
                    src={selectedCamera?.streamUrl}
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="bg-gray-900 rounded-lg p-3">
                    <div className="text-sm text-gray-400 mb-1">Status</div>
                    <div className="text-white font-semibold flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      <span>Active</span>
                    </div>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-3">
                    <div className="text-sm text-gray-400 mb-1">Uptime</div>
                    <div className="text-white font-semibold">{selectedCamera?.uptime}</div>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-3">
                    <div className="text-sm text-gray-400 mb-1">AI Detections</div>
                    <div className="text-white font-semibold">{selectedCamera?.aiDetections} Active</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}